import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mic,
  BrainCircuit,
  Send,
  ChevronRight,
  FileText,
  Sheet,
  CalendarClock,
  RefreshCw,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Mark } from '../components/Logo';

const STEPS = [
  {
    icon: Mic,
    kicker: 'Record',
    title: 'Capture the call',
    body: 'The Call Recorder runs quietly in the background and saves every conversation as clean audio — no note-taking mid-call.',
  },
  {
    icon: BrainCircuit,
    kicker: 'Analyze',
    title: 'AI does the write-up',
    body: 'The Call Processor transcribes on your machine, then drafts a summary, objections, and the recommended next move.',
  },
  {
    icon: Send,
    kicker: 'Follow up',
    title: 'Act on the next step',
    body: 'Get a ready-to-send packet, a synced Google Sheet row, and a calendar hold — before your next call connects.',
  },
];

const FEATURES = [
  { icon: FileText, title: 'Follow-up packets', body: 'A per-call brief with summary, sentiment, and the next action drafted for you.' },
  { icon: Sheet, title: 'Synced Google Sheet', body: 'Every call lands as a row in your pipeline sheet through a simple Apps Script hook.' },
  { icon: CalendarClock, title: 'Calendar holds', body: 'The recommended follow-up drops onto your calendar so nothing slips a day.' },
  { icon: RefreshCw, title: 'Auto-updating apps', body: 'Both desktop apps check for a new version on launch and update themselves. Fail-open, never blocking.' },
  { icon: ShieldCheck, title: 'Transcription on your machine', body: 'Audio is transcribed locally with Whisper — recordings stay on the device you run.' },
  { icon: Clock, title: 'Built for back-to-back', body: 'Designed for reps who finish one call and dial the next — the write-up is done by then.' },
];

function StepFlow() {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-stretch">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.kicker}>
          <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-accent/15 dark:text-accent">
                <s.icon size={18} />
              </span>
              <span className="font-display text-sm font-semibold text-muted-foreground">
                {s.kicker}
              </span>
            </div>
            <p className="mt-3 text-sm font-medium text-foreground">{s.title}</p>
          </div>
          {i < STEPS.length - 1 && (
            <div className="flex items-center justify-center py-1 sm:py-0">
              <ChevronRight
                className="rotate-90 text-accent sm:rotate-0"
                size={22}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grid-backdrop opacity-70" aria-hidden />
        <div className="container-page relative py-20 md:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                <Mark size={16} />
                Free during launch — no card required
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl">
                Turn every call into <span className="text-gradient">the next step</span>
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
                Next Step Followup records your sales calls, transcribes and analyzes them with AI,
                and hands you a ready follow-up — a packet, a sheet row, and a calendar hold — before
                the next call rings.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/signup">
                  <Button size="lg">Create free account</Button>
                </Link>
                <Link to="/setup">
                  <Button variant="outline" size="lg">
                    See how setup works
                  </Button>
                </Link>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                Windows Call Recorder + Call Processor. You keep the recordings.
              </p>
            </div>

            {/* Hero visual: the three-step flow, the product's core loop */}
            <div className="animate-fade-up [animation-delay:120ms]">
              <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-5 shadow-lift">
                <div className="flex items-center justify-between px-1 pb-4">
                  <span className="font-display text-sm font-semibold text-foreground">
                    Call → next step
                  </span>
                  <span className="text-xs text-muted-foreground">auto, per call</span>
                </div>
                <StepFlow />
                <div className="mt-4 rounded-xl border border-border bg-background/60 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Next step drafted
                  </p>
                  <p className="mt-1.5 text-sm text-foreground">
                    “Send the Q3 proposal and book a 15-min review Thursday. Address the pricing
                    objection with the annual plan.”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-card/40">
        <div className="container-page py-20">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Three steps, and the follow-up writes itself
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              You run two small Windows apps. Everything between hanging up and the next hello is
              handled for you.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.kicker} className="relative rounded-2xl border border-border bg-card p-7 shadow-card">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <s.icon size={20} />
                  </span>
                  <div className="font-display text-sm font-semibold text-muted-foreground">
                    Step {i + 1} · {s.kicker}
                  </div>
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-t border-border">
        <div className="container-page py-20">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Everything you need after “thanks, talk soon”
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              The pieces that usually get skipped when you are moving fast — done automatically.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent/12 text-accent">
                  <f.icon size={18} />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-foreground">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="container-page py-20">
          <div className="relative overflow-hidden rounded-2xl bg-primary px-8 py-14 text-center shadow-lift">
            <div className="pointer-events-none absolute inset-0 grid-backdrop opacity-20" aria-hidden />
            <div className="relative">
              <h2 className="font-display text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl">
                Start turning calls into next steps
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80">
                Create a free account, download both apps, and follow the setup guide. Free during
                launch — Pro comes later.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link to="/signup">
                  <Button variant="accent" size="lg">
                    Create free account
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-primary-foreground/25 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
                  >
                    View pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
